import React, { Suspense } from "react";

import type {
	ArchivedProjectsRequest,
	ArchivedProjectsResponse,
} from "@/features/archive/model/archive/ArchivedProjectsRequest";
import HistoryTable from "@/features/archive/ui/components/history/HistoryTable";
import Spinner from "@/shared/ui/Spinner";
import ComponentError from "@/shared/ui/errors/Components";
import { Box } from "@mui/material";
import * as Sentry from "@sentry/react";
import { QueryErrorResetBoundary } from "@tanstack/react-query";

type Props = {
	title?: React.ReactNode;
	subtitle?: React.ReactNode;
	options: ArchivedProjectsRequest;

	initialRowsPerPage?: number;
	embeddedInProcedurePage?: boolean;
	containerHeight?: number;

	archivedProcedures?: ArchivedProjectsResponse | null;
	isLoadingArchived?: boolean;
};

export default function HistoryContainer({
	title,
	subtitle,
	options,
	initialRowsPerPage = 10,
	embeddedInProcedurePage = false,
	containerHeight,
	archivedProcedures,
	isLoadingArchived,
}: Props) {
	return (
		<QueryErrorResetBoundary>
			{({ reset }) => (
				<Sentry.ErrorBoundary
					onReset={() => reset()}
					fallback={(fallbackProps) => (
						<Box sx={{ px: 2, py: 2 }}>
							<ComponentError
								message={
									fallbackProps.error instanceof Error
										? fallbackProps.error.message
										: String(fallbackProps.error)
								}
								reset={() => fallbackProps.resetError()}
							/>
						</Box>
					)}
				>
					<Suspense fallback={<Spinner text={"Loading history"} />}>
						<HistoryTable
							title={title}
							subtitle={subtitle}
							initialRowsPerPage={initialRowsPerPage}
							options={options}
							embeddedInProcedurePage={embeddedInProcedurePage}
							containerHeight={containerHeight}
							archivedProceduresFromParent={archivedProcedures}
							isLoadingFromParent={isLoadingArchived}
						/>
					</Suspense>
				</Sentry.ErrorBoundary>
			)}
		</QueryErrorResetBoundary>
	);
}
